// File: frontend/src/pages/settings/SettingsPage.tsx
// src/components/settings/SettingsPage.tsx

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useTheme, ThemeType } from "../../context/ThemeContext";
import { extractClubColors } from "../../utils/fanTheme";
import { usePreferences } from "../../context/PreferencesContext";
import { Navigate } from "react-router-dom";
import { UserPreferenceDto } from "../../models/UserPreferenceModel";
import { ThemeEnum } from "../../models/enums/ThemeEnum";
import { FaSun, FaMoon, FaScroll, FaLemon, FaCookieBite, FaFish, FaCloud, FaHeart, FaWater, FaLock } from "react-icons/fa";
import { requestAccountDeletion, cancelAccountDeletion } from "../../services/accountDeletionService";
import { fetchBlockedList, unblockUser, BlockedUserItem } from "../../services/blockService";
import { Pagination } from "../../utils/Pagination";
import { requestAccountFreeze, cancelAccountFreeze } from "../../services/accountFreezeService";
import { changePassword } from "../../services/passwordService";
import { changeEmail } from "../../services/emailService";
import { fetchProfileById, getMyProfileDetails, updateMyProfileDetails } from "../../services/profileService";
import { getPublicCountries, MdCountryDto } from "../../services/masterDataService";
import { clubService } from "../../services/clubService";
import { FavoriteClubSummary, ProfileDetailsModel } from "../../models/ProfileModel";
import { ClubModel } from "../../models/ClubModel";
import {
  ACCOUNT_DELETION_CANCEL_COOLDOWN_HOURS,
  ACCOUNT_FREEZE_CANCEL_COOLDOWN_HOURS,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MAX_LENGTH,
} from "../../constants";
import { useCooldownTimer } from "../../hooks/useCooldownTimer";
import { parseApiError } from "../../utils/parseApiError";
import { InputClearButton } from "../../components/common/InputClearButton";
import {
  getPushSupportStatus,
  isSubscribedToPush,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
} from "../../utils/pushNotifications";
import "./SettingsPage.css";
import { parseServerDate } from "../../utils/dateUtils";

// Tab types
type SettingsTab = "preferences" | "profile" | "password" | "email" | "account";

// Preference sub-tab types — "entry" is the new tab added for entry-specific settings
// "mobile" sub-tab has been removed; its settings are reserved for future use
type PreferenceSubTab = "display" | "entry" | "rookie" | "messaging" | "notifications" | "privacy" | "blocked";

export const SettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, user, logout, updateUserStatus } = useAuth();
  const { theme, setTheme, setFanThemeSource } = useTheme();
  const { preferences, loading, error: preferencesError, updateUserPreferences } = usePreferences();
  const [activeTab, setActiveTab] = useState<SettingsTab>("preferences");
  const [activePreferenceSubTab, setActivePreferenceSubTab] = useState<PreferenceSubTab>("display");

  // Local saving state
  const [saving, setSaving] = useState(false);

  // ── Push notification toggle state (Ticket 2) ──
  // This is not part of UserPreferenceDto — it reflects the browser's own
  // subscription state (Notification permission + PushManager subscription),
  // not a server-side preference, so it is not wired through renderToggle/handleToggle.
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const pushSupportStatus = getPushSupportStatus();

  useEffect(() => {
    if (pushSupportStatus === "ready") {
      isSubscribedToPush().then(setPushSubscribed);
    }
    // Only needs to run once on mount to read the current browser subscription state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePushToggle = async (checked: boolean) => {
    setPushError(null);
    setPushLoading(true);
    try {
      if (checked) {
        await subscribeToPushNotifications();
        setPushSubscribed(true);
      } else {
        await unsubscribeFromPushNotifications();
        setPushSubscribed(false);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message === "PUSH_PERMISSION_DENIED") {
        setPushError(t("pref_push_notifications_denied"));
      } else {
        setPushError(t("pref_push_notifications_error"));
      }
      setPushSubscribed(false);
    } finally {
      setPushLoading(false);
    }
  };

  // ── Password change state ──
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  // ── Email change state ──
  const [emailPassword, setEmailPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [showEmailPw, setShowEmailPw] = useState(false);

  // ── Account deletion state ──
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteEntries, setDeleteEntries] = useState(false);
  const [hideEntriesDuringGrace, setHideEntriesDuringGrace] = useState(false);
  const [deletionError, setDeletionError] = useState<string | null>(null);
  const [deletionLoading, setDeletionLoading] = useState(false);
  // Scheduled deletion date — fetched from profile when status is DELETED_BY_USER
  const [scheduledDeletionAt, setScheduledDeletionAt] = useState<string | null>(null);
  const [deletionRequestedAt, setDeletionRequestedAt] = useState<string | null>(null);

  // ── Account freeze state ──
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [freezeError, setFreezeError] = useState<string | null>(null);
  const [freezeLoading, setFreezeLoading] = useState(false);
  // When the freeze was requested — fetched from profile when status is FROZEN_BY_USER
  const [freezeRequestedAt, setFreezeRequestedAt] = useState<string | null>(null);

  // ── Profile tab state ──
  const [, setProfileDetails] = useState<ProfileDetailsModel | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);

  // Form fields for profile tab
  const [profileBirthday, setProfileBirthday] = useState("");
  const [profileGender, setProfileGender] = useState("");
  // "TR" = Turkey dropdown, "ABROAD" = country dropdown + free text city
  const [locationType, setLocationType] = useState<"TR" | "ABROAD">("TR");
  const [profileCity, setProfileCity] = useState("");
  // ISO country code for abroad selection (from md_country master data)
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const [profileAbroadCity, setProfileAbroadCity] = useState("");
  // Country list loaded from master data (excludes Turkey — handled by the TR toggle)
  const [countries, setCountries] = useState<MdCountryDto[]>([]);

  // Favorite clubs picker state
  const [favoriteClubs, setFavoriteClubs] = useState<FavoriteClubSummary[]>([]);
  const [clubSearchQuery, setClubSearchQuery] = useState("");
  const [clubSearchResults, setClubSearchResults] = useState<ClubModel[]>([]);
  const [clubSearchLoading, setClubSearchLoading] = useState(false);
  const [clubPickerOpen, setClubPickerOpen] = useState(false);

  // ── Blocked users list state (Settings → Engellemeler tab) ──
  const [blockedList, setBlockedList] = useState<BlockedUserItem[]>([]);
  const [blockedLoading, setBlockedLoading] = useState(false);
  const [blockedError, setBlockedError] = useState<string | null>(null);
  const [blockedPage, setBlockedPage] = useState(0);
  const [blockedTotalPages, setBlockedTotalPages] = useState(0);
  const [unblockingId, setUnblockingId] = useState<number | null>(null);

  // Compute deletion cancellation cooldown
  // Append "Z" so the browser treats the server timestamp as UTC, not local time
  const deletionCancelableAt = deletionRequestedAt
    ? new Date(parseServerDate(deletionRequestedAt).getTime() + ACCOUNT_DELETION_CANCEL_COOLDOWN_HOURS * 3600000)
    : null;
  const { isCoolingDown: isDeletionCoolingDown, remainingLabel: deletionRemainingLabel } =
    useCooldownTimer(deletionCancelableAt);

  // Compute freeze cancellation cooldown (from settings page; login always unfreezes immediately)
  // Append "Z" so the browser treats the server timestamp as UTC, not local time
  const freezeCancelableAt = freezeRequestedAt
    ? new Date(parseServerDate(freezeRequestedAt).getTime() + ACCOUNT_FREEZE_CANCEL_COOLDOWN_HOURS * 3600000)
    : null;
  const { isCoolingDown: isFreezeCoolingDown, remainingLabel: freezeRemainingLabel } =
    useCooldownTimer(freezeCancelableAt);

  // If the user already has a pending deletion or active freeze, load dates from profile
  useEffect(() => {
    if (!user?.id) return;

    if (user.status === "DELETED_BY_USER" || user.status === "FROZEN_BY_USER") {
      fetchProfileById(user.id)
        .then((p) => {
          if (user.status === "DELETED_BY_USER") {
            setScheduledDeletionAt(p.scheduledDeletionAt);
            setDeletionRequestedAt(p.deletionRequestedAt);
          }
          if (user.status === "FROZEN_BY_USER") {
            setFreezeRequestedAt(p.freezeRequestedAt);
          }
        })
        .catch(() => {});
    }
  }, [user?.status, user?.id]);

  // Pushes the FIRST favorite club's colours into the theme layer. The taraftar
  // theme is driven entirely by this: no club (or a club with no verified
  // colours on record) means the option is not offered at all.
  const syncFanThemeSource = (clubs: FavoriteClubSummary[]) => {
    const primaryClub = clubs[0] ?? null;
    const colors = extractClubColors(primaryClub);
    if (colors.length === 0) {
      setFanThemeSource(null);
      return;
    }
    setFanThemeSource({
      clubId: primaryClub?.id ?? null,
      clubName: primaryClub?.shortName ?? null,
      colors,
    });
  };

  // Load profile details and clubs once the settings page opens.
  // This is not limited to the profile tab any more: the Görünüm (display) tab
  // needs the favorite clubs too, because the taraftar theme is only offered
  // when the user actually has a favorite club to take colours from.
  useEffect(() => {
    if (!user?.id) return;
    setProfileLoading(true);
    setProfileError(null);
    Promise.all([
      getMyProfileDetails(),
      getPublicCountries(),
    ])
      .then(([details, countryList]) => {
        setProfileDetails(details);
        // Populate form fields from loaded data
        setProfileBirthday(details.birthday ?? "");
        setProfileGender(details.gender ?? "");
        setFavoriteClubs(details.favoriteClubs ?? []);
        // Feed the first favorite club's colours into the theme layer so the
        // taraftar option can appear (and repaint) without a page reload.
        syncFanThemeSource(details.favoriteClubs ?? []);
        // All active countries excluding Turkey (handled separately by TR toggle)
        const abroad = countryList.filter((c) => c.code !== "TR");
        setCountries(abroad);
        // Determine location type and pre-select country for abroad users
        if (!details.country || details.country === "TR") {
          setLocationType("TR");
          setProfileCity(details.city ?? "");
        } else {
          setLocationType("ABROAD");
          // Pre-select the stored country code; fall back to first abroad country
          const storedCode = details.country ?? "";
          const isKnown = abroad.some((c) => c.code === storedCode);
          setSelectedCountryCode(isKnown ? storedCode : (abroad[0]?.code ?? ""));
          setProfileAbroadCity(details.city ?? "");
        }
      })
      .catch(() => setProfileError(t("profile_settings_load_error")))
      .finally(() => setProfileLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Search clubs via backend when the user types (min 2 chars)
  useEffect(() => {
    const q = clubSearchQuery.trim();
    if (q.length < 2) {
      setClubSearchResults([]);
      return;
    }
    let cancelled = false;
    setClubSearchLoading(true);
    clubService.searchClubs(q)
      .then((results) => { if (!cancelled) setClubSearchResults(results); })
      .catch(() => { if (!cancelled) setClubSearchResults([]); })
      .finally(() => { if (!cancelled) setClubSearchLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubSearchQuery]);

  // Redirect to home if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  // Handle tab change
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
  };

  // Handle preference sub-tab change
  const handlePreferenceSubTabChange = (subTab: PreferenceSubTab) => {
    setActivePreferenceSubTab(subTab);
  };

  // Handle theme change
  const handleThemeChange = async (newTheme: ThemeEnum) => {
    try {
      setSaving(true);

      // Update via context (updates backend and local state)
      await updateUserPreferences({ theme: newTheme });

      // Update frontend theme context
      const themeMap: Record<ThemeEnum, ThemeType> = {
        [ThemeEnum.LIGHT]: "light",
        [ThemeEnum.DARK]: "dark",
        [ThemeEnum.PAPER]: "paper",
        [ThemeEnum.TANGO]: "tango",
        [ThemeEnum.FLAMINGO]: "flamingo",
        [ThemeEnum.COMOLOKKO]: "comolokko",
        [ThemeEnum.LOKUM]: "lokum",
        [ThemeEnum.MERCAN]: "mercan",
        [ThemeEnum.GOKYUZU]: "gokyuzu",
        [ThemeEnum.TARAFTAR]: "taraftar",
      };
      setTheme(themeMap[newTheme]);
    } catch (err) {
      console.error("Failed to update theme:", err);
    } finally {
      setSaving(false);
    }
  };

  // Handle boolean preference toggle
  const handleToggle = async (key: keyof UserPreferenceDto, value: boolean) => {
    try {
      setSaving(true);
      await updateUserPreferences({ [key]: value });
    } catch (err) {
      console.error(`Failed to update ${key}:`, err);
    } finally {
      setSaving(false);
    }
  };

  // Handle numeric page size preference change
  const handlePageSizeChange = async (key: "entriesPerPage" | "matchesPerPage", value: number) => {
    try {
      setSaving(true);
      await updateUserPreferences({ [key]: value });
    } catch (err) {
      console.error(`Failed to update ${key}:`, err);
    } finally {
      setSaving(false);
    }
  };

  // Render page size selector (10 / 20 / 30 / 50)
  const renderPageSizeSelector = (
    key: "entriesPerPage" | "matchesPerPage",
    label: string,
    description: string
  ) => {
    const current = preferences?.[key] ?? (key === "entriesPerPage" ? 10 : 10);
    const options = [10, 20, 30, 50];
    return (
      <div className="preference-item">
        <div className="preference-info">
          <span className="preference-label">{label}</span>
          <span className="preference-description">{description}</span>
        </div>
        <div className="page-size-selector">
          {options.map((opt) => (
            <button
              key={opt}
              className={`page-size-option${current === opt ? " active" : ""}`}
              onClick={() => handlePageSizeChange(key, opt)}
              disabled={saving}
              type="button"
            >
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render preference sub-tabs
  const renderPreferenceSubTabs = () => (
    <div className="preference-subtabs">
      <button
        className={`preference-subtab ${activePreferenceSubTab === "display" ? "active" : ""}`}
        onClick={() => handlePreferenceSubTabChange("display")}
      >
        {t("pref_subtab_display")}
      </button>
      {/* Entry settings sub-tab — houses showEntryIds and permanentDelete */}
      <button
        className={`preference-subtab ${activePreferenceSubTab === "entry" ? "active" : ""}`}
        onClick={() => handlePreferenceSubTabChange("entry")}
      >
        {t("pref_subtab_entry")}
      </button>
      <button
        className={`preference-subtab ${activePreferenceSubTab === "rookie" ? "active" : ""}`}
        onClick={() => handlePreferenceSubTabChange("rookie")}
      >
        {t("pref_subtab_rookie")}
      </button>
      <button
        className={`preference-subtab ${activePreferenceSubTab === "messaging" ? "active" : ""}`}
        onClick={() => handlePreferenceSubTabChange("messaging")}
      >
        {t("pref_subtab_messaging")}
      </button>
      <button
        className={`preference-subtab ${activePreferenceSubTab === "notifications" ? "active" : ""}`}
        onClick={() => handlePreferenceSubTabChange("notifications")}
      >
        {t("pref_subtab_notifications")}
      </button>
      <button
        className={`preference-subtab ${activePreferenceSubTab === "privacy" ? "active" : ""}`}
        onClick={() => handlePreferenceSubTabChange("privacy")}
      >
        {t("pref_subtab_privacy")}
      </button>
      {/* Mobile sub-tab removed — mobile-specific settings will appear here in a future release */}
      <button
        className={`preference-subtab ${activePreferenceSubTab === "blocked" ? "active" : ""}`}
        onClick={() => {
          handlePreferenceSubTabChange("blocked");
          // Load the first page when switching to this tab
          loadBlockedList(0);
        }}
      >
        {t("pref_subtab_blocked")}
      </button>
    </div>
  );

  // Render toggle switch component
  const renderToggle = (
    key: keyof UserPreferenceDto,
    label: string,
    description?: string,
    disabled?: boolean
  ) => {
    const value = preferences?.[key] as boolean;
    return (
      <div className={`preference-item ${disabled ? "disabled" : ""}`}>
        <div className="preference-info">
          <span className="preference-label">{label}</span>
          {description && <span className="preference-description">{description}</span>}
        </div>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => !disabled && handleToggle(key, e.target.checked)}
            disabled={disabled || saving}
          />
          <span className="toggle-slider"></span>
        </label>
        {disabled && <span className="coming-soon-badge">{t("settings_coming_soon_badge")}</span>}
      </div>
    );
  };

  // Colours of the first favorite club — drives both the taraftar swatch and
  // whether the option is shown at all.
  const fanThemeColors = extractClubColors(favoriteClubs[0] ?? null);

  // Render display settings — theme + matches per page
  const renderDisplaySettings = () => (
    <div className="preference-section">
      <h4>{t("pref_section_theme")}</h4>
      <div className="theme-selector">
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.LIGHT ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.LIGHT)}
          disabled={saving}
        >
          <span className="theme-icon"><FaSun size={18} /></span>
          <span>{t("theme_light")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.DARK ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.DARK)}
          disabled={saving}
        >
          <span className="theme-icon"><FaMoon size={18} /></span>
          <span>{t("theme_dark")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.PAPER ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.PAPER)}
          disabled={saving}
        >
          <span className="theme-icon"><FaScroll size={18} /></span>
          <span>{t("theme_paper")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.TANGO ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.TANGO)}
          disabled={saving}
        >
          <span className="theme-icon"><FaLemon size={18} /></span>
          <span>{t("theme_tango")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.FLAMINGO ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.FLAMINGO)}
          disabled={saving}
        >
          <span className="theme-icon"><FaHeart size={18} /></span>
          <span>{t("theme_flamingo")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.COMOLOKKO ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.COMOLOKKO)}
          disabled={saving}
        >
          <span className="theme-icon"><FaWater size={18} /></span>
          <span>{t("theme_comolokko")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.LOKUM ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.LOKUM)}
          disabled={saving}
        >
          <span className="theme-icon"><FaCookieBite size={18} /></span>
          <span>{t("theme_lokum")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.MERCAN ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.MERCAN)}
          disabled={saving}
        >
          <span className="theme-icon"><FaFish size={18} /></span>
          <span>{t("theme_mercan")}</span>
        </button>
        <button
          className={`theme-option ${preferences?.theme === ThemeEnum.GOKYUZU ? "active" : ""}`}
          onClick={() => handleThemeChange(ThemeEnum.GOKYUZU)}
          disabled={saving}
        >
          <span className="theme-icon"><FaCloud size={18} /></span>
          <span>{t("theme_gokyuzu")}</span>
        </button>
        {/* Taraftar (fan) theme — rendered only when the user has a favorite
            club with colours on record, since its whole palette comes from
            that club. The swatch previews the exact colours that will be used. */}
        {fanThemeColors.length > 0 ? (
          <button
            className={`theme-option ${preferences?.theme === ThemeEnum.TARAFTAR ? "active" : ""}`}
            onClick={() => handleThemeChange(ThemeEnum.TARAFTAR)}
            disabled={saving}
            title={favoriteClubs[0] ? getClubDisplayName(favoriteClubs[0]) : undefined}
          >
            <span className="theme-icon">
              <span className="fan-theme-swatch">
                {fanThemeColors.map((color, i) => (
                  <span key={i} className="fan-theme-swatch-part" style={{ backgroundColor: color }} />
                ))}
              </span>
            </span>
            <span>{t("theme_taraftar")}</span>
          </button>
        ) : (
          /* No favorite club yet: the tile still occupies its place in the grid
             so the theme list does not appear to be missing one, but it cannot
             be selected and says what unlocks it. */
          <button
            className="theme-option theme-option--locked"
            disabled
            type="button"
            title={t("theme_taraftar_locked")}
          >
            <span className="theme-icon"><FaLock size={16} /></span>
            <span>{t("theme_taraftar")}</span>
          </button>
        )}
      </div>
      {fanThemeColors.length === 0 && (
        <p className="theme-locked-hint">{t("theme_taraftar_locked")}</p>
      )}
      <h4 style={{ marginTop: "var(--spacing-lg)" }}>{t("pref_section_pagination")}</h4>
      {renderPageSizeSelector("matchesPerPage", t("pref_matches_per_page"), t("pref_matches_per_page_desc"))}
    </div>
  );

  // Render entry settings — showEntryIds + permanentDelete + entries per page
  const renderEntrySettings = () => (
    <div className="preference-section">
      <h4>{t("pref_section_entry")}</h4>
      {renderToggle("showEntryIds", t("pref_show_entry_ids"), t("pref_show_entry_ids_desc"))}
      {renderToggle("permanentDelete", t("pref_permanent_delete"), t("pref_permanent_delete_desc"))}
      {renderPageSizeSelector("entriesPerPage", t("pref_entries_per_page"), t("pref_entries_per_page_desc"))}
    </div>
  );

  // Render rookie settings
  const renderRookieSettings = () => (
    <div className="preference-section">
      <h4>{t("pref_section_rookie")}</h4>
      {renderToggle("showRookieEntries", t("pref_show_rookie_entries"), t("pref_show_rookie_entries_desc"))}
    </div>
  );

  // Render messaging settings — messagePrivacy radio selector
  const renderMessagingSettings = () => {
    /*
     * Ordered from the most open inbox to the most closed one, so the list reads
     * as a single scale instead of four unrelated choices.
     *
     * FOLLOWERS means the people THIS USER follows may write to them — the
     * direction the label promises ("Sadece Takip Ettiklerim"), and the direction
     * MessageService enforces. It is not "my followers may write to me".
     */
    const privacyOptions: { value: string; label: string; desc: string }[] = [
      { value: "EVERYONE",   label: t("pref_msg_privacy_everyone"),   desc: t("pref_msg_privacy_everyone_desc") },
      { value: "AUTHORS",    label: t("pref_msg_privacy_authors"),    desc: t("pref_msg_privacy_authors_desc") },
      { value: "FOLLOWERS",  label: t("pref_msg_privacy_followers"),  desc: t("pref_msg_privacy_followers_desc") },
      { value: "NOBODY",     label: t("pref_msg_privacy_nobody"),     desc: t("pref_msg_privacy_nobody_desc") },
    ];

    const handlePrivacyChange = async (value: string) => {
      try {
        setSaving(true);
        await updateUserPreferences({ messagePrivacy: value as any });
      } catch (err) {
        console.error("Failed to update messagePrivacy:", err);
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="preference-section">
        <h4>{t("pref_section_messaging")}</h4>
        {privacyOptions.map((opt) => (
          <div
            key={opt.value}
            className={`preference-item preference-item--radio ${preferences?.messagePrivacy === opt.value ? "selected" : ""}`}
            onClick={() => !saving && handlePrivacyChange(opt.value)}
          >
            <div className="preference-info">
              <span className="preference-label">{opt.label}</span>
              <span className="preference-description">{opt.desc}</span>
            </div>
            <input
              type="radio"
              name="messagePrivacy"
              value={opt.value}
              checked={preferences?.messagePrivacy === opt.value}
              onChange={() => !saving && handlePrivacyChange(opt.value)}
              disabled={saving}
            />
          </div>
        ))}
      
      </div>
    );
  };

  // Render notification settings
  // notifyOnVote/Favorite/EntryDeleted/AccountStatus are fully wired on the backend.
  // The emailNotifications toggle was removed from the UI — email delivery is not
  // implemented, so the row only advertised a feature that does nothing. The
  // preference field itself is kept on the model/backend for a later rollout.
  const renderNotificationSettings = () => (
    <div className="preference-section">
      <h4>{t("pref_section_notifications")}</h4>
      {pushSupportStatus === "unsupported" ? (
        <div className="preference-item disabled">
          <div className="preference-info">
            <span className="preference-label">{t("pref_push_notifications")}</span>
            <span className="preference-description">{t("pref_push_notifications_unsupported")}</span>
          </div>
        </div>
      ) : (
        <div className="preference-item">
          <div className="preference-info">
            <span className="preference-label">{t("pref_push_notifications")}</span>
            <span className="preference-description">
              {pushError || t("pref_push_notifications_desc")}
            </span>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={pushSubscribed}
              onChange={(e) => handlePushToggle(e.target.checked)}
              disabled={pushLoading}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      )}
      {renderToggle("notifyOnVote",          t("pref_notify_on_vote"),           t("pref_notify_on_vote_desc"))}
      {renderToggle("notifyOnFavorite",      t("pref_notify_on_favorite"),       t("pref_notify_on_favorite_desc"))}
      {renderToggle("notifyOnEntryDeleted",  t("pref_notify_on_entry_deleted"),  t("pref_notify_on_entry_deleted_desc"))}
      {renderToggle("notifyOnAccountStatus", t("pref_notify_on_account_status"), t("pref_notify_on_account_status_desc"))}
      {renderToggle("notifyOnNewFollower",    t("pref_notify_on_new_follower"),    t("pref_notify_on_new_follower_desc"))}
    </div>
  );

  // Render privacy settings — permanentDelete removed (moved to Entry tab)
  const renderPrivacySettings = () => (
    <div className="preference-section">
      <h4>{t("pref_section_privacy")}</h4>
      {/* showOnlineStatus: fully wired — controls visibility in the online users list */}
      {renderToggle("showOnlineStatus", t("pref_show_online_status"), t("pref_show_online_status_desc"))}
      {/* hideFromSearch: fully wired — excludes this user from the author search results */}
      {renderToggle("hideFromSearch", t("pref_hide_from_search"), t("pref_hide_from_search_desc"))}
    </div>
  );

  // renderMobileSettings has been removed; the "Mobil" sub-tab is no longer displayed.
  // doubleTapVote and other mobile preferences remain on the entity for future use.

  // Render preference sub-tab content
  const renderPreferenceSubTabContent = () => {
    if (loading) {
      return <p className="settings-placeholder">{t("loading")}</p>;
    }

    if (preferencesError) {
      return <p className="settings-error">{preferencesError}</p>;
    }

    switch (activePreferenceSubTab) {
      case "display":
        return renderDisplaySettings();
      case "entry":
        return renderEntrySettings();
      case "rookie":
        return renderRookieSettings();
      case "messaging":
        return renderMessagingSettings();
      case "notifications":
        return renderNotificationSettings();
      case "privacy":
        return renderPrivacySettings();
      // "mobile" case removed — sub-tab no longer rendered
      case "blocked":
        return renderBlockedSettings();
      default:
        return null;
    }
  };

  // ── Blocked users list (Settings → Engellemeler tab) ──

  const loadBlockedList = async (page: number) => {
    setBlockedLoading(true);
    setBlockedError(null);
    try {
      const data = await fetchBlockedList(page, 20);
      setBlockedList(data.content);
      setBlockedPage(page);
      setBlockedTotalPages(data.totalPages);
    } catch {
      setBlockedError(t("block_list_load_error"));
    } finally {
      setBlockedLoading(false);
    }
  };

  const handleUnblockFromSettings = async (userId: number) => {
    if (unblockingId !== null) return;
    setUnblockingId(userId);
    try {
      await unblockUser(userId);
      // Remove from local list immediately
      setBlockedList((prev) => prev.filter((u) => u.userId !== userId));
    } catch {
      // Silently ignore — user can refresh the tab
    } finally {
      setUnblockingId(null);
    }
  };

  const renderBlockedSettings = () => (
    <div className="preference-section">
      <h4>{t("pref_section_blocked")}</h4>
      <p className="preference-description">{t("pref_blocked_desc")}</p>

      {blockedLoading && (
        <p className="settings-placeholder">{t("loading")}</p>
      )}

      {blockedError && (
        <p className="settings-error">{blockedError}</p>
      )}

      {!blockedLoading && !blockedError && blockedList.length === 0 && (
        <p className="settings-placeholder">{t("block_list_empty")}</p>
      )}

      {!blockedLoading && blockedList.length > 0 && (
        <div className="blocked-user-list">
          {blockedList.map((u) => (
            <div key={u.userId} className="blocked-user-row">
              <span className="blocked-user-row__name">{u.username}</span>
              <button
                className="blocked-user-row__unblock-btn"
                onClick={() => handleUnblockFromSettings(u.userId)}
                disabled={unblockingId === u.userId}
                type="button"
              >
                {unblockingId === u.userId ? "..." : t("block_unblock")}
              </button>
            </div>
          ))}

          {/* Shared Pagination component — same control as every other list.
              It is 1-indexed while the loader keeps 0-indexed pages. */}
          {blockedTotalPages > 1 && (
            <div className="blocked-list-pagination">
              <Pagination
                currentPage={blockedPage + 1}
                totalPages={blockedTotalPages}
                totalItems={blockedList.length}
                paginate={(page) => loadBlockedList(page - 1)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ── Freeze section renderer ──
  const renderFreezeSection = () => {
    const isFrozen = user?.status === "FROZEN_BY_USER";

    return (
      <div className="preference-section">
        <h4>{t("account_freeze_title")}</h4>

        {isFrozen ? (
          /* Active freeze state */
          <div className="preference-item">
            <div className="preference-info">
              <span className="preference-label">{t("account_frozen_label")}</span>
              <span className="preference-description">{t("account_frozen_desc")}</span>
              <span className="account-messaging-note">{t("account_messaging_disabled_note")}</span>
            </div>
            {isFreezeCoolingDown ? (
              <span className="deletion-cooldown-badge">
                {t("account_cancel_cooldown_remaining", { time: freezeRemainingLabel })}
              </span>
            ) : (
              <button
                className="account-cancel-btn"
                onClick={async () => {
                  setFreezeLoading(true);
                  setFreezeError(null);
                  try {
                    await cancelAccountFreeze();
                    // Restore status locally — backend resolves AUTHOR vs ROOKIE on next request
                    updateUserStatus("AUTHOR");
                    setFreezeRequestedAt(null);
                  } catch (err: any) {
                    setFreezeError(err.message || t("account_freeze_error"));
                  } finally {
                    setFreezeLoading(false);
                  }
                }}
                disabled={freezeLoading}
                type="button"
              >
                {freezeLoading ? t("account_freeze_loading") : t("account_unfreeze_btn")}
              </button>
            )}
          </div>
        ) : (
          /* Normal state — freeze request button */
          <div className="preference-item">
            <div className="preference-info">
              <span className="preference-label">{t("account_freeze_title")}</span>
              <span className="preference-description">{t("account_freeze_desc")}</span>
              <span className="account-messaging-note">{t("account_messaging_disabled_note")}</span>
            </div>
            <button
              className="account-freeze-btn"
              onClick={() => { setShowFreezeModal(true); setFreezeError(null); }}
              // Disable freeze button when a deletion is already pending
              disabled={user?.status === "DELETED_BY_USER"}
              type="button"
            >
              {t("account_freeze_btn")}
            </button>
          </div>
        )}

        {freezeError && (
          <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-error, #e74c3c)", marginTop: "var(--spacing-sm)" }}>
            {freezeError}
          </p>
        )}
      </div>
    );
  };

  // ── Delete section renderer ──
  const renderDeleteSection = () => (
    <div className="preference-section">
      <h4>{t("account_delete_section_title")}</h4>
      {user?.status === "DELETED_BY_USER" ? (
        /* Pending deletion state */
        <div className="preference-item">
          <div className="preference-info">
            <span className="preference-label">{t("account_delete_pending_label")}</span>
            <span className="preference-description">
              {scheduledDeletionAt
                ? t("account_delete_pending_desc", {
                    date: parseServerDate(scheduledDeletionAt).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    }),
                  })
                : t("account_delete_pending_desc_nodate")}
            </span>
            <span className="account-messaging-note">{t("account_messaging_disabled_note")}</span>
          </div>
          {isDeletionCoolingDown ? (
            <span className="deletion-cooldown-badge">
              {t("account_cancel_cooldown_remaining", { time: deletionRemainingLabel })}
            </span>
          ) : (
            <button
              className="account-cancel-btn"
              onClick={async () => {
                setDeletionLoading(true);
                try {
                  await cancelAccountDeletion();
                  updateUserStatus("AUTHOR");
                  setScheduledDeletionAt(null);
                  setDeletionRequestedAt(null);
                } catch (err: any) {
                  setDeletionError(err.message || t("account_delete_error"));
                } finally {
                  setDeletionLoading(false);
                }
              }}
              disabled={deletionLoading}
              type="button"
            >
              {deletionLoading ? t("account_delete_loading") : t("account_delete_pending_cancel_btn")}
            </button>
          )}
        </div>
      ) : (
        /* Normal state — delete request button */
        <div className="preference-item">
          <div className="preference-info">
            <span className="preference-label">{t("account_delete_title")}</span>
            <span className="preference-description">{t("account_delete_desc", { days: 14 })}</span>
          </div>
          <button
            className="account-delete-btn"
            onClick={() => { setShowDeleteModal(true); setDeletionError(null); }}
            type="button"
          >
            {t("account_delete_request_btn")}
          </button>
        </div>
      )}
      {deletionError && (
        <p style={{ fontSize: "var(--font-size-sm)", color: "var(--color-error, #e74c3c)", marginTop: "var(--spacing-sm)" }}>
          {deletionError}
        </p>
      )}
    </div>
  );

  // ── Password change renderer ──
  // ── Profile tab ──

  const TURKISH_CITIES = [
    "Adana", "Adıyaman", "Afyonkarahisar", "Ağrı", "Aksaray", "Amasya",
    "Ankara", "Antalya", "Ardahan", "Artvin", "Aydın", "Balıkesir",
    "Bartın", "Batman", "Bayburt", "Bilecik", "Bingöl", "Bitlis",
    "Bolu", "Burdur", "Bursa", "Çanakkale", "Çankırı", "Çorum",
    "Denizli", "Diyarbakır", "Düzce", "Edirne", "Elazığ", "Erzincan",
    "Erzurum", "Eskişehir", "Gaziantep", "Giresun", "Gümüşhane",
    "Hakkari", "Hatay", "Iğdır", "Isparta", "İstanbul", "İzmir",
    "Kahramanmaraş", "Karabük", "Karaman", "Kars", "Kastamonu",
    "Kayseri", "Kilis", "Kırıkkale", "Kırklareli", "Kırşehir",
    "Kocaeli", "Konya", "Kütahya", "Malatya", "Manisa", "Mardin",
    "Mersin", "Muğla", "Muş", "Nevşehir", "Niğde", "Ordu",
    "Osmaniye", "Rize", "Sakarya", "Samsun", "Siirt", "Sinop",
    "Sivas", "Şanlıurfa", "Şırnak", "Tekirdağ", "Tokat", "Trabzon",
    "Tunceli", "Uşak", "Van", "Yalova", "Yozgat", "Zonguldak",
  ];

  const MAX_FAVORITE_CLUBS = 5;

  const handleProfileSave = async () => {
    setProfileSaving(true);
    setProfileSuccess(false);
    setProfileError(null);
    try {
      const city    = locationType === "TR" ? profileCity : profileAbroadCity;
      const country = locationType === "TR" ? "TR" : selectedCountryCode;
      await updateMyProfileDetails({
        birthday: profileBirthday || null,
        gender:   profileGender   || null,
        city:     city            || null,
        country:  country         || null,
        favoriteClubIds: favoriteClubs.map((c) => c.id),
      });
      setProfileSuccess(true);

      // Keep the taraftar theme honest after a favorite-club change: repaint
      // with the new primary club, or leave the theme entirely when the user
      // removed every club (or the remaining one has no colours).
      syncFanThemeSource(favoriteClubs);
      if (extractClubColors(favoriteClubs[0] ?? null).length === 0 && theme === "taraftar") {
        setTheme("light");
        try {
          await updateUserPreferences({ theme: ThemeEnum.LIGHT });
        } catch {
          // Preference sync is best effort — the local theme already changed.
        }
      }
    } catch {
      setProfileError(t("profile_settings_save_error"));
    } finally {
      setProfileSaving(false);
    }
  };

  const handleAddFavoriteClub = (club: ClubModel) => {
    if (favoriteClubs.length >= MAX_FAVORITE_CLUBS) return;
    if (favoriteClubs.some((c) => c.id === club.id)) return;
    setFavoriteClubs((prev) => [
      ...prev,
      { id: club.id, slug: club.slug, shortName: club.shortName, nameTr: club.nameTr, nameEn: club.nameEn, logoUrl: club.logoUrl, color1: club.color1, color2: club.color2, color3: club.color3 },
    ]);
    setClubSearchQuery("");
  };

  const handleRemoveFavoriteClub = (clubId: number) => {
    setFavoriteClubs((prev) => prev.filter((c) => c.id !== clubId));
  };

  const getClubDisplayName = (club: { shortName: string; nameTr: string | null; nameEn: string | null }) => {
    const locale = i18n.language;
    if (locale.startsWith("tr") && club.nameTr) return club.nameTr;
    if (locale.startsWith("en") && club.nameEn) return club.nameEn;
    return club.shortName;
  };

  const renderProfileTab = () => {
    if (profileLoading) {
      return (
        <div className="settings-tab-content">
          <p className="settings-placeholder">{t("profile_settings_loading")}</p>
        </div>
      );
    }

    return (
      <div className="settings-tab-content">
        <h3>{t("profile_settings_title")}</h3>

        {/* ── Personal Information ── */}
        <div className="preference-section">
          <h4>{t("profile_settings_personal")}</h4>

          {/* Birthday — three dropdowns to avoid browser locale issues with native date picker */}
          <div className="profile-field-group">
            <label className="profile-field-label">{t("profile_settings_birthday")}</label>
            <div className="profile-birthday-row">
              {/* Day */}
              <select
                className="profile-field-input profile-birthday-select"
                value={profileBirthday ? profileBirthday.split("-")[2] : ""}
                onChange={(e) => {
                  const parts = profileBirthday ? profileBirthday.split("-") : ["", "", ""];
                  const newVal = e.target.value ? `${parts[0] || ""}-${parts[1] || ""}-${e.target.value}` : "";
                  setProfileBirthday(newVal);
                  setProfileSuccess(false);
                }}
              >
                <option value="">{t("profile_settings_birthday_day")}</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={String(d).padStart(2, "0")}>{d}</option>
                ))}
              </select>

              {/* Month */}
              <select
                className="profile-field-input profile-birthday-select"
                value={profileBirthday ? profileBirthday.split("-")[1] : ""}
                onChange={(e) => {
                  const parts = profileBirthday ? profileBirthday.split("-") : ["", "", ""];
                  const newVal = e.target.value ? `${parts[0] || ""}-${e.target.value}-${parts[2] || ""}` : "";
                  setProfileBirthday(newVal);
                  setProfileSuccess(false);
                }}
              >
                <option value="">{t("profile_settings_birthday_month")}</option>
                {[
                  t("month_jan"), t("month_feb"), t("month_mar"), t("month_apr"),
                  t("month_may"), t("month_jun"), t("month_jul"), t("month_aug"),
                  t("month_sep"), t("month_oct"), t("month_nov"), t("month_dec"),
                ].map((name, idx) => (
                  <option key={idx + 1} value={String(idx + 1).padStart(2, "0")}>{name}</option>
                ))}
              </select>

              {/* Year */}
              <select
                className="profile-field-input profile-birthday-select"
                value={profileBirthday ? profileBirthday.split("-")[0] : ""}
                onChange={(e) => {
                  const parts = profileBirthday ? profileBirthday.split("-") : ["", "", ""];
                  const newVal = e.target.value ? `${e.target.value}-${parts[1] || ""}-${parts[2] || ""}` : "";
                  setProfileBirthday(newVal);
                  setProfileSuccess(false);
                }}
              >
                <option value="">{t("profile_settings_birthday_year")}</option>
                {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Gender */}
          <div className="profile-field-group">
            <label className="profile-field-label">{t("profile_settings_gender")}</label>
            <select
              className="profile-field-input"
              value={profileGender}
              onChange={(e) => { setProfileGender(e.target.value); setProfileSuccess(false); }}
            >
              <option value="">{t("profile_settings_gender_unspecified")}</option>
              <option value="MALE">{t("profile_settings_gender_male")}</option>
              <option value="FEMALE">{t("profile_settings_gender_female")}</option>
              <option value="OTHER">{t("profile_settings_gender_other")}</option>
            </select>
          </div>
        </div>

        {/* ── Location ── */}
        <div className="preference-section">
          <h4>{t("profile_settings_location")}</h4>

          {/* Turkey / Abroad toggle */}
          <div className="profile-location-toggle">
            <button
              type="button"
              className={`profile-location-btn ${locationType === "TR" ? "active" : ""}`}
              onClick={() => { setLocationType("TR"); setProfileSuccess(false); }}
            >
              {t("profile_settings_location_tr")}
            </button>
            <button
              type="button"
              className={`profile-location-btn ${locationType === "ABROAD" ? "active" : ""}`}
              onClick={() => { setLocationType("ABROAD"); setProfileSuccess(false); }}
            >
              {t("profile_settings_location_abroad")}
            </button>
          </div>

          {locationType === "TR" ? (
            /* Turkey: city dropdown */
            <div className="profile-field-group">
              <label className="profile-field-label">{t("profile_settings_city_tr")}</label>
              <select
                className="profile-field-input"
                value={profileCity}
                onChange={(e) => { setProfileCity(e.target.value); setProfileSuccess(false); }}
              >
                <option value="">{t("profile_settings_city_placeholder")}</option>
                {TURKISH_CITIES.map((city) => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          ) : (
            /* Abroad: country dropdown from master data + free text city */
            <>
              <div className="profile-field-group">
                <label className="profile-field-label">{t("profile_settings_country")}</label>
                <select
                  className="profile-field-input"
                  value={selectedCountryCode}
                  onChange={(e) => { setSelectedCountryCode(e.target.value); setProfileSuccess(false); }}
                >
                  <option value="">{t("profile_settings_country_placeholder")}</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="profile-field-group">
                <label className="profile-field-label">{t("profile_settings_city_abroad")}</label>
                <input
                  type="text"
                  className="profile-field-input"
                  placeholder={t("profile_settings_city_abroad_placeholder")}
                  value={profileAbroadCity}
                  maxLength={100}
                  onChange={(e) => { setProfileAbroadCity(e.target.value); setProfileSuccess(false); }}
                />
              </div>
            </>
          )}
        </div>

        {/* ── Favorite Clubs ── */}
        <div className="preference-section">
          <h4>{t("profile_settings_clubs")}</h4>
          <p className="preference-description">{t("profile_settings_clubs_desc", { max: MAX_FAVORITE_CLUBS })}</p>

          {/* Selected clubs as chips */}
          {favoriteClubs.length > 0 && (
            <div className="profile-clubs-chips">
              {favoriteClubs.map((club) => (
                <div key={club.id} className="profile-club-chip">
                  {club.logoUrl && (
                    <img
                      src={club.logoUrl}
                      alt={club.shortName}
                      className="profile-club-chip__logo"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <span className="profile-club-chip__name">{getClubDisplayName(club)}</span>
                  <button
                    type="button"
                    className="profile-club-chip__remove"
                    onClick={() => { handleRemoveFavoriteClub(club.id); setProfileSuccess(false); }}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Club search picker */}
          {favoriteClubs.length < MAX_FAVORITE_CLUBS && (
            <div className="profile-clubs-picker">
              <input
                type="text"
                className="profile-field-input"
                placeholder={t("profile_settings_clubs_search")}
                value={clubSearchQuery}
                onChange={(e) => { setClubSearchQuery(e.target.value); setClubPickerOpen(true); }}
                onFocus={() => setClubPickerOpen(true)}
                onBlur={() => setTimeout(() => setClubPickerOpen(false), 150)}
              />
              {clubSearchQuery !== "" && (
                <InputClearButton onClear={() => setClubSearchQuery("")} />
              )}
              {clubPickerOpen && clubSearchQuery.trim().length >= 2 && (
                <div className="profile-clubs-dropdown">
                  {clubSearchLoading && (
                    <p className="profile-clubs-dropdown__empty">{t("loading")}</p>
                  )}
                  {!clubSearchLoading && clubSearchResults
                    .filter((c) => !favoriteClubs.some((f) => f.id === c.id))
                    .slice(0, 20)
                    .map((club) => (
                      <button
                        key={club.id}
                        type="button"
                        className="profile-clubs-dropdown__item"
                        onMouseDown={() => handleAddFavoriteClub(club)}
                      >
                        {club.logoUrl && (
                          <img
                            src={club.logoUrl}
                            alt={club.shortName}
                            className="profile-clubs-dropdown__logo"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                          />
                        )}
                        <span>{getClubDisplayName(club)}</span>
                      </button>
                    ))}
                  {!clubSearchLoading && clubSearchResults.filter((c) => !favoriteClubs.some((f) => f.id === c.id)).length === 0 && (
                    <p className="profile-clubs-dropdown__empty">{t("profile_settings_clubs_no_results")}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Save button ── */}
        {profileError && (
          <p className="profile-settings-error">{profileError}</p>
        )}
        {profileSuccess && (
          <p className="profile-settings-success">{t("profile_settings_save_success")}</p>
        )}
        <div className="profile-settings-actions">
          <button
            type="button"
            className="pw-submit-btn"
            onClick={handleProfileSave}
            disabled={profileSaving}
          >
            {profileSaving ? t("saving") : t("profile_settings_save")}
          </button>
        </div>
      </div>
    );
  };

  const renderPasswordTab = () => {
    const getStrength = (pw: string) => {
      if (pw.length === 0) return null;
      if (pw.length < PASSWORD_MIN_LENGTH) return "weak";
      const hasUpper = /[A-Z]/.test(pw);
      const hasNum   = /[0-9]/.test(pw);
      const hasSpec  = /[^A-Za-z0-9]/.test(pw);
      const score = [hasUpper, hasNum, hasSpec].filter(Boolean).length;
      if (pw.length >= 10 && score >= 2) return "strong";
      return "medium";
    };

    const strength = getStrength(newPassword);

    const handleSubmit = async () => {
      setPasswordError(null);
      setPasswordSuccess(false);

      if (newPassword.length < PASSWORD_MIN_LENGTH) {
        setPasswordError(t("password_change_error_min", { min: PASSWORD_MIN_LENGTH }));
        return;
      }
      // Upper bound mirrors the backend so the rule fails here, not after submit
      if (newPassword.length > PASSWORD_MAX_LENGTH) {
        setPasswordError(t("password_change_error_max", { max: PASSWORD_MAX_LENGTH }));
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordError(t("password_change_error_mismatch"));
        return;
      }

      setPasswordSaving(true);
      try {
        await changePassword(currentPassword, newPassword);
        setPasswordSuccess(true);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } catch (err: any) {
        const msg: string = err.message || "";
        setPasswordError(parseApiError(msg, t));
      } finally {
        setPasswordSaving(false);
      }
    };

    return (
      <div className="settings-tab-content">
        <div className="preference-section">
          <h4>{t("password_change_title")}</h4>

          {passwordSuccess && (
            <div className="pw-success-banner">{t("password_change_success")}</div>
          )}

          <div className="pw-field-group">
            <label className="pw-label">{t("password_current")}</label>
            <div className="pw-input-wrap">
              <input
                type={showCurrentPw ? "text" : "password"}
                className="pw-input"
                placeholder={t("password_current_placeholder")}
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPasswordError(null); setPasswordSuccess(false); }}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="pw-toggle-visibility"
                onClick={() => setShowCurrentPw(v => !v)}
                tabIndex={-1}
              >
                {showCurrentPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          <div className="pw-field-group">
            <label className="pw-label">{t("password_new")}</label>
            <div className="pw-input-wrap">
              <input
                type={showNewPw ? "text" : "password"}
                className="pw-input"
                placeholder={t("password_new_placeholder")}
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordError(null); setPasswordSuccess(false); }}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="pw-toggle-visibility"
                onClick={() => setShowNewPw(v => !v)}
                tabIndex={-1}
              >
                {showNewPw ? "🙈" : "👁"}
              </button>
            </div>
            {/* Rules are stated up-front, not only after a failed submit.
                NOTE: no maxLength attribute on purpose — see RegisterModal.tsx */}
            <span className={`pw-hint${newPassword.length > PASSWORD_MAX_LENGTH ? " pw-hint--over" : ""}`}>
              {t("password_rules_hint", { min: PASSWORD_MIN_LENGTH, max: PASSWORD_MAX_LENGTH })}
              {newPassword.length > 0 && ` (${newPassword.length}/${PASSWORD_MAX_LENGTH})`}
            </span>
            {strength && (
              <div className={`pw-strength pw-strength--${strength}`}>
                <span className="pw-strength-bar" />
                <span className="pw-strength-label">{t(`password_strength_${strength}`)}</span>
              </div>
            )}
          </div>

          <div className="pw-field-group">
            <label className="pw-label">{t("password_confirm")}</label>
            <div className="pw-input-wrap">
              <input
                type={showConfirmPw ? "text" : "password"}
                className="pw-input"
                placeholder={t("password_confirm_placeholder")}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(null); setPasswordSuccess(false); }}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="pw-toggle-visibility"
                onClick={() => setShowConfirmPw(v => !v)}
                tabIndex={-1}
              >
                {showConfirmPw ? "🙈" : "👁"}
              </button>
            </div>
            {confirmPassword.length > 0 && (
              <span className={`pw-match-hint ${newPassword === confirmPassword ? "pw-match-hint--ok" : "pw-match-hint--no"}`}>
                {newPassword === confirmPassword ? t("passwords_match") : t("register_error_passwords_not_match")}
              </span>
            )}
          </div>

          {passwordError && (
            <p className="pw-error">{passwordError}</p>
          )}

          <button
            className="pw-submit-btn"
            onClick={handleSubmit}
            disabled={passwordSaving || !currentPassword || !newPassword || !confirmPassword}
            type="button"
          >
            {passwordSaving ? t("password_change_saving") : t("password_change_btn")}
          </button>
        </div>
      </div>
    );
  };

  // ── Email change renderer ──
  const renderEmailTab = () => {
    // Basic email format validation
    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleEmailSubmit = async () => {
      setEmailError(null);
      setEmailSuccess(false);

      if (!isValidEmail(newEmail)) {
        setEmailError(t("email_change_error_invalid"));
        return;
      }
      if (newEmail !== confirmEmail) {
        setEmailError(t("email_change_error_mismatch"));
        return;
      }

      setEmailSaving(true);
      try {
        await changeEmail(emailPassword, newEmail);
        setEmailSuccess(true);
        setEmailPassword("");
        setNewEmail("");
        setConfirmEmail("");
      } catch (err: any) {
        const msg: string = err.message || "";
        setEmailError(parseApiError(msg, t));
      } finally {
        setEmailSaving(false);
      }
    };

    return (
      <div className="settings-tab-content">
        <div className="preference-section">
          <h4>{t("email_change_title")}</h4>

          {emailSuccess && (
            <div className="pw-success-banner">{t("email_change_success")}</div>
          )}

          {/* Show current email as read-only info */}
          <div className="pw-field-group">
            <label className="pw-label">{t("email_current")}</label>
            <div className="pw-input-wrap">
              <input
                type="email"
                className="pw-input"
                value={user.email ?? ""}
                readOnly
                disabled
                autoComplete="off"
              />
            </div>
          </div>

          <div className="pw-field-group">
            <label className="pw-label">{t("email_new")}</label>
            <div className="pw-input-wrap">
              <input
                type="email"
                className="pw-input"
                placeholder={t("email_new_placeholder")}
                value={newEmail}
                onChange={(e) => { setNewEmail(e.target.value); setEmailError(null); setEmailSuccess(false); }}
                autoComplete="email"
              />
            </div>
          </div>

          <div className="pw-field-group">
            <label className="pw-label">{t("email_confirm")}</label>
            <div className="pw-input-wrap">
              <input
                type="email"
                className="pw-input"
                placeholder={t("email_confirm_placeholder")}
                value={confirmEmail}
                onChange={(e) => { setConfirmEmail(e.target.value); setEmailError(null); setEmailSuccess(false); }}
                autoComplete="email"
              />
            </div>
            {/* Show match hint only when the confirm field has content */}
            {confirmEmail.length > 0 && (
              <span className={`pw-match-hint ${newEmail === confirmEmail ? "pw-match-hint--ok" : "pw-match-hint--no"}`}>
                {newEmail === confirmEmail ? t("passwords_match") : t("email_change_error_mismatch")}
              </span>
            )}
          </div>

          {/* Password field for identity verification */}
          <div className="pw-field-group">
            <label className="pw-label">{t("email_current_password")}</label>
            <div className="pw-input-wrap">
              <input
                type={showEmailPw ? "text" : "password"}
                className="pw-input"
                placeholder={t("email_current_password_placeholder")}
                value={emailPassword}
                onChange={(e) => { setEmailPassword(e.target.value); setEmailError(null); setEmailSuccess(false); }}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="pw-toggle-visibility"
                onClick={() => setShowEmailPw(v => !v)}
                tabIndex={-1}
              >
                {showEmailPw ? "🙈" : "👁"}
              </button>
            </div>
          </div>

          {emailError && (
            <p className="pw-error">{emailError}</p>
          )}

          <button
            className="pw-submit-btn"
            onClick={handleEmailSubmit}
            disabled={emailSaving || !emailPassword || !newEmail || !confirmEmail}
            type="button"
          >
            {emailSaving ? t("email_change_saving") : t("email_change_btn")}
          </button>
        </div>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "preferences":
        return (
          <div className="settings-tab-content">
            {renderPreferenceSubTabs()}
            <div className="preference-content">
              {renderPreferenceSubTabContent()}
            </div>
          </div>
        );
      case "profile":
        return renderProfileTab();
      case "password":
        return renderPasswordTab();
      case "email":
        return renderEmailTab();
      case "account":
        return (
          <div className="settings-tab-content">
            {/* ── Freeze account ── */}
            {renderFreezeSection()}

            {/* ── Delete account ── */}
            {renderDeleteSection()}

            {/* ── Freeze confirmation modal ── */}
            {showFreezeModal && (
              <div className="deletion-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowFreezeModal(false) } }}>
                <div className="deletion-modal">
                  <h3 className="deletion-modal__title">{t("account_freeze_confirm_title")}</h3>
                  <p className="deletion-modal__body">{t("account_freeze_confirm_body")}</p>

                  {freezeError && <p className="deletion-modal__error">{freezeError}</p>}

                  <div className="deletion-modal__actions">
                    <button
                      className="deletion-modal__cancel"
                      onClick={() => setShowFreezeModal(false)}
                      type="button"
                      disabled={freezeLoading}
                    >
                      {t("account_delete_cancel")}
                    </button>
                    <button
                      className="deletion-modal__confirm deletion-modal__confirm--freeze"
                      onClick={async () => {
                        setFreezeLoading(true);
                        setFreezeError(null);
                        try {
                          await requestAccountFreeze();
                          setShowFreezeModal(false);
                          // Log out so the freeze takes effect; next login auto-unfreezes
                          logout();
                        } catch (err: any) {
                          setFreezeError(err.message || t("account_freeze_error"));
                        } finally {
                          setFreezeLoading(false);
                        }
                      }}
                      type="button"
                      disabled={freezeLoading}
                    >
                      {freezeLoading ? t("account_freeze_loading") : t("account_freeze_confirm_btn")}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Deletion confirmation modal ── */}
            {showDeleteModal && (
              <div className="deletion-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) { setShowDeleteModal(false) } }}>
                <div className="deletion-modal">
                  <h3 className="deletion-modal__title">{t("account_delete_confirm_title")}</h3>
                  <p className="deletion-modal__body">{t("account_delete_confirm_body", { days: 14 })}</p>

                  {/* Toggle 1: delete entries with account */}
                  <div className="preference-item deletion-modal__toggle-row">
                    <div className="preference-info">
                      <span className="preference-label">{t("account_delete_entries_label")}</span>
                      <span className="preference-description">{t("account_delete_entries_desc")}</span>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={deleteEntries}
                        onChange={(e) => {
                          setDeleteEntries(e.target.checked);
                          // Reset second toggle if first is turned off
                          if (!e.target.checked) setHideEntriesDuringGrace(false);
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {/* Toggle 2: hide during grace — only visible when deleteEntries is on */}
                  {deleteEntries && (
                    <div className="preference-item deletion-modal__toggle-row">
                      <div className="preference-info">
                        <span className="preference-label">{t("account_hide_entries_grace_label")}</span>
                        <span className="preference-description">{t("account_hide_entries_grace_desc")}</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={hideEntriesDuringGrace}
                          onChange={(e) => setHideEntriesDuringGrace(e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  )}

                  {deletionError && <p className="deletion-modal__error">{deletionError}</p>}

                  <div className="deletion-modal__actions">
                    <button
                      className="deletion-modal__cancel"
                      onClick={() => setShowDeleteModal(false)}
                      type="button"
                      disabled={deletionLoading}
                    >
                      {t("account_delete_cancel")}
                    </button>
                    <button
                      className="deletion-modal__confirm"
                      onClick={async () => {
                        setDeletionLoading(true);
                        setDeletionError(null);
                        try {
                          await requestAccountDeletion(deleteEntries, hideEntriesDuringGrace);
                          setShowDeleteModal(false);
                          // Log the user out after deletion request is submitted
                          logout();
                        } catch (err: any) {
                          setDeletionError(err.message || t("account_delete_error"));
                        } finally {
                          setDeletionLoading(false);
                        }
                      }}
                      type="button"
                      disabled={deletionLoading}
                    >
                      {deletionLoading ? t("account_delete_loading") : t("account_delete_confirm_btn")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="settings-container">
      {/* Page Title */}
      <div className="settings-header">
        <h1 className="settings-title">{t("settings_title")}</h1>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        <button
          className={`settings-tab ${activeTab === "preferences" ? "active" : ""}`}
          onClick={() => handleTabChange("preferences")}
        >
          {t("settings_tab_preferences")}
        </button>
        <button
          className={`settings-tab ${activeTab === "profile" ? "active" : ""}`}
          onClick={() => handleTabChange("profile")}
        >
          {t("settings_tab_profile")}
        </button>
        <button
          className={`settings-tab ${activeTab === "password" ? "active" : ""}`}
          onClick={() => handleTabChange("password")}
        >
          {t("settings_tab_password")}
        </button>
        {/* Email change tab — placed right after password */}
        <button
          className={`settings-tab ${activeTab === "email" ? "active" : ""}`}
          onClick={() => handleTabChange("email")}
        >
          {t("settings_tab_email")}
        </button>
        <button
          className={`settings-tab ${activeTab === "account" ? "active" : ""}`}
          onClick={() => handleTabChange("account")}
        >
          {t("settings_tab_account")}
        </button>
      </div>

      {/* Tab Content */}
      <div className="settings-content">
        {renderTabContent()}
      </div>
    </div>
  );
};